// pages/admin/BuildingManage.tsx
/**
 * @mockup /design/mockups/05-desktop-admin-dashboard.png
 * @description Building management page with responsive unit CRUD
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ExternalLink,
  Calendar,
  BarChart2,
  QrCode,
  AlertTriangle,
  Loader2,
  Users,
  Star,
  Mail,
  Phone,
  Settings,
  Layers,
  ImageIcon
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Modal, Input, Select } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { ImagePicker } from '../../components/admin/ImagePicker';
import { AmenitiesEditor } from '../../components/admin/AmenitiesEditor';
import { formatPrice, formatDate } from '../../lib/utils';
import {
  getBuilding,
  getUnitTypes,
  getLeasingAgents,
  createLeasingAgent,
  updateLeasingAgent,
  deleteLeasingAgent,
  updateBuilding,
  getTemplates,
  type Building,
  type Unit,
  type UnitType,
  type LeasingAgent,
  type UnitTemplate
} from '../../lib/api';

export function BuildingManage() {
  const { slug } = useParams<{ slug: string }>();
  const { showToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [deletingUnit, setDeletingUnit] = useState<Unit | null>(null);
  const [filter, setFilter] = useState<'all' | 'available' | 'rented'>('all');

  // Data from API
  const [building, setBuilding] = useState<Building | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [templates, setTemplates] = useState<UnitTemplate[]>([]);
  const [agents, setAgents] = useState<LeasingAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Agent modal state
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<LeasingAgent | null>(null);
  const [isDeleteAgentModalOpen, setIsDeleteAgentModalOpen] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState<LeasingAgent | null>(null);

  // Building edit modal state
  const [isBuildingModalOpen, setIsBuildingModalOpen] = useState(false);

  // Fetch building and units from API
  useEffect(() => {
    async function fetchData() {
      if (!slug) return;

      setLoading(true);
      setError(null);

      const [buildingResponse, unitTypesResponse] = await Promise.all([
        getBuilding(slug),
        getUnitTypes()
      ]);

      if (buildingResponse.error) {
        setError(buildingResponse.error);
        setLoading(false);
        return;
      }

      if (buildingResponse.data) {
        setBuilding(buildingResponse.data.building);
        setUnits(buildingResponse.data.units);

        // Fetch agents and templates for this building
        const [agentsResponse, templatesResponse] = await Promise.all([
          getLeasingAgents(buildingResponse.data.building.id),
          getTemplates(buildingResponse.data.building.id)
        ]);

        if (agentsResponse.data) {
          setAgents(agentsResponse.data.agents);
        }
        if (templatesResponse.data) {
          setTemplates(templatesResponse.data.templates);
        }
      }

      if (unitTypesResponse.data) {
        setUnitTypes(unitTypesResponse.data.unit_types);
      }

      setLoading(false);
    }

    fetchData();
  }, [slug]);

  const availableUnits = units.filter((u) => u.is_available);

  // Filter units based on selected filter
  const filteredUnits = units.filter((u) => {
    if (filter === 'available') return u.is_available;
    if (filter === 'rented') return !u.is_available;
    return true;
  });

  const handleAddUnit = () => {
    setEditingUnit(null);
    setIsModalOpen(true);
  };

  const handleEditUnit = (unit: Unit) => {
    setEditingUnit(unit);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (unit: Unit) => {
    setDeletingUnit(unit);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingUnit) {
      setUnits((prev) => prev.filter((u) => u.id !== deletingUnit.id));
      showToast(`Unit #${deletingUnit.unit_number} deleted`, 'success');
      setIsDeleteModalOpen(false);
      setDeletingUnit(null);
      // TODO: Delete from Supabase
    }
  };

  const handleSaveUnit = (formData: UnitFormData) => {
    if (!building) return;

    // Find template for additional fields
    const selectedTemplate = formData.template_id
      ? templates.find((t) => t.id === formData.template_id)
      : null;

    if (editingUnit) {
      // Update existing
      setUnits((prev) =>
        prev.map((u) =>
          u.id === editingUnit.id
            ? {
                ...u,
                ...formData,
                unit_type: unitTypes.find(
                  (t) => t.id === formData.unit_type_id
                ) || null,
              }
            : u
        )
      );
      showToast(`Unit #${formData.unit_number} updated`, 'success');
    } else {
      // Add new - apply template defaults if present
      const newUnit: Unit = {
        id: `unit-${Date.now()}`,
        building_id: building.id,
        ...formData,
        is_available: true,
        features: selectedTemplate?.default_features || [],
        virtual_tour_url: null,
        utilities_included: false,
        utilities_estimate: null,
        parking_cost: null,
        key_deposit: null,
        accessibility_features: [],
        promotion_override: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        unit_type: unitTypes.find((t) => t.id === formData.unit_type_id) || null,
      };
      setUnits((prev) => [...prev, newUnit]);
      showToast(`Unit #${formData.unit_number} added`, 'success');
    }
    setIsModalOpen(false);
    // TODO: Save to API
  };

  // Agent handlers
  const handleAddAgent = () => {
    setEditingAgent(null);
    setIsAgentModalOpen(true);
  };

  const handleEditAgent = (agent: LeasingAgent) => {
    setEditingAgent(agent);
    setIsAgentModalOpen(true);
  };

  const handleDeleteAgentClick = (agent: LeasingAgent) => {
    setDeletingAgent(agent);
    setIsDeleteAgentModalOpen(true);
  };

  const handleConfirmDeleteAgent = async () => {
    if (!deletingAgent || !building) return;

    const { error } = await deleteLeasingAgent(building.id, deletingAgent.id);
    if (error) {
      showToast(`Failed to delete agent: ${error}`, 'error');
    } else {
      setAgents((prev) => prev.filter((a) => a.id !== deletingAgent.id));
      showToast(`${deletingAgent.name} removed`, 'success');
    }
    setIsDeleteAgentModalOpen(false);
    setDeletingAgent(null);
  };

  const handleSaveAgent = async (formData: AgentFormData) => {
    if (!building) return;

    if (editingAgent) {
      // Update existing
      const { error } = await updateLeasingAgent(building.id, editingAgent.id, formData);
      if (error) {
        showToast(`Failed to update agent: ${error}`, 'error');
        return;
      }
      setAgents((prev) =>
        prev.map((a) =>
          a.id === editingAgent.id ? { ...a, ...formData } : a
        )
      );
      showToast(`${formData.name} updated`, 'success');
    } else {
      // Create new
      const { data, error } = await createLeasingAgent(building.id, formData);
      if (error) {
        showToast(`Failed to add agent: ${error}`, 'error');
        return;
      }
      if (data) {
        const newAgent: LeasingAgent = {
          id: data.id,
          building_id: building.id,
          ...formData,
          phone: formData.phone || null,
          title: formData.title || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        setAgents((prev) => [...prev, newAgent]);
        showToast(`${formData.name} added`, 'success');
      }
    }
    setIsAgentModalOpen(false);
  };

  // Building edit handler
  const handleSaveBuilding = async (formData: BuildingFormData) => {
    if (!building || !slug) return;

    const { error } = await updateBuilding(slug, formData);
    if (error) {
      showToast(`Failed to update building: ${error}`, 'error');
      return;
    }

    setBuilding((prev) => prev ? { ...prev, ...formData } : null);
    showToast('Building updated successfully', 'success');
    setIsBuildingModalOpen(false);
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-gray-600">Loading building...</span>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error || !building) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Building Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'Unable to load building data.'}</p>
          <Link to="/admin" className="text-primary hover:underline">
            ← Back to Dashboard
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Breadcrumb */}
        <div className="mb-4 lg:mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back to Dashboard
          </Link>
        </div>

        {/* Page Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">
                {building.name}
              </h1>
              <button
                onClick={() => setIsBuildingModalOpen(true)}
                className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
                title="Edit building"
              >
                <Settings size={18} />
              </button>
            </div>
            <p className="text-sm lg:text-base text-gray-500 mt-1">{building.address}</p>
          </div>
          <a
            href={`/b/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-hover self-start"
          >
            View Public Page
            <ExternalLink size={14} />
          </a>
        </div>

        {/* Quick Actions - Horizontal scroll on mobile */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 lg:mx-0 lg:px-0 mb-6 lg:mb-8">
          <Link to={`/admin/buildings/${slug}/templates`}>
            <Button variant="secondary" size="sm" className="whitespace-nowrap">
              <Layers size={16} className="mr-2" />
              Templates
            </Button>
          </Link>
          <Link to={`/admin/buildings/${slug}/viewings`}>
            <Button variant="secondary" size="sm" className="whitespace-nowrap">
              <Calendar size={16} className="mr-2" />
              Viewings
            </Button>
          </Link>
          <Link to={`/admin/buildings/${slug}/analytics`}>
            <Button variant="secondary" size="sm" className="whitespace-nowrap">
              <BarChart2 size={16} className="mr-2" />
              Analytics
            </Button>
          </Link>
          <Link to={`/admin/buildings/${slug}/qr`}>
            <Button variant="secondary" size="sm" className="whitespace-nowrap">
              <QrCode size={16} className="mr-2" />
              QR Code
            </Button>
          </Link>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3 lg:gap-6 mb-6 lg:mb-8">
          <StatCard label="Available Units" value={availableUnits.length.toString()} />
          <StatCard label="QR Scans" value="127" />
          <StatCard label="Email Clicks" value="24" />
        </div>

        {/* Units Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 lg:gap-4">
              <h2 className="text-base lg:text-lg font-semibold text-gray-900">Units</h2>
              {/* Filter Tabs - Scrollable on mobile */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {(['all', 'available', 'rented'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-2.5 lg:px-3 py-1 text-xs lg:text-sm rounded-md transition-colors whitespace-nowrap ${
                      filter === f
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {f === 'all' ? 'All' : f === 'available' ? 'Available' : 'Rented'}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleAddUnit} size="sm" className="w-full sm:w-auto">
              <Plus size={16} className="mr-2" />
              Add Unit
            </Button>
          </div>

          {/* Desktop: Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Floor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Available Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUnits.map((unit) => (
                  <tr key={unit.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {unit.unit_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {unit.floor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {unit.unit_type?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatPrice(unit.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {unit.availability_date
                        ? formatDate(unit.availability_date)
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          unit.is_available
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {unit.is_available ? 'Available' : 'Rented'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEditUnit(unit)}
                        className="text-primary hover:text-primary-hover p-1 mr-2"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      {unit.is_available && (
                        <button
                          onClick={() => handleDeleteClick(unit)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Cards */}
          <div className="lg:hidden divide-y divide-gray-100">
            {filteredUnits.map((unit) => (
              <div key={unit.id} className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">Unit {unit.unit_number}</p>
                    <p className="text-sm text-gray-500">Floor {unit.floor} • {unit.unit_type?.name}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    unit.is_available ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {unit.is_available ? 'Available' : 'Rented'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mb-3">
                  <div>
                    <p className="text-gray-500">Price</p>
                    <p className="font-medium text-gray-900">{formatPrice(unit.price)}/mo</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-500">Available</p>
                    <p className="font-medium text-gray-900">
                      {unit.availability_date ? formatDate(unit.availability_date) : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleEditUnit(unit)}
                    className="flex-1 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 active:bg-blue-200 transition-colors"
                  >
                    Edit
                  </button>
                  {unit.is_available && (
                    <button
                      onClick={() => handleDeleteClick(unit)}
                      className="flex-1 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 active:bg-red-200 transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 lg:px-6 py-3 border-t border-gray-100 text-sm text-gray-500">
            Showing {filteredUnits.length} of {units.length} units
          </div>
        </div>

        {/* Leasing Agents Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-6">
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-gray-500" />
              <h2 className="text-base lg:text-lg font-semibold text-gray-900">
                Leasing Agents ({agents.length})
              </h2>
            </div>
            <Button onClick={handleAddAgent} size="sm">
              <Plus size={16} className="mr-2" />
              Add Agent
            </Button>
          </div>

          {agents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users size={32} className="mx-auto mb-2 opacity-50" />
              <p>No leasing agents assigned to this building.</p>
              <p className="text-sm">Add an agent to display contact info on the public page.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {agents.map((agent) => (
                <div key={agent.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-medium text-gray-600">
                        {agent.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{agent.name}</p>
                        {agent.is_primary && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Star size={10} />
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        {agent.title && <span>{agent.title}</span>}
                        <span className="flex items-center gap-1">
                          <Mail size={12} />
                          {agent.email}
                        </span>
                        {agent.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={12} />
                            {agent.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditAgent(agent)}
                      className="text-primary hover:text-primary-hover p-2"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteAgentClick(agent)}
                      className="text-red-500 hover:text-red-700 p-2"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Unit Modal */}
      <UnitFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveUnit}
        unit={editingUnit}
        unitTypes={unitTypes}
        templates={templates}
        slug={slug || ''}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Unit"
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} className="w-full sm:w-auto">
              Delete Unit
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <p className="text-gray-900">
              Are you sure you want to delete <strong>Unit #{deletingUnit?.unit_number}</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-1">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Agent Modal */}
      <AgentFormModal
        isOpen={isAgentModalOpen}
        onClose={() => setIsAgentModalOpen(false)}
        onSave={handleSaveAgent}
        agent={editingAgent}
      />

      {/* Delete Agent Confirmation Modal */}
      <Modal
        isOpen={isDeleteAgentModalOpen}
        onClose={() => setIsDeleteAgentModalOpen(false)}
        title="Remove Agent"
        footer={
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
            <Button variant="ghost" onClick={() => setIsDeleteAgentModalOpen(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDeleteAgent} className="w-full sm:w-auto">
              Remove Agent
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-4">
          <div className="p-2 bg-red-100 rounded-full flex-shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <p className="text-gray-900">
              Are you sure you want to remove <strong>{deletingAgent?.name}</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-1">
              This will remove them from this building's contact list.
            </p>
          </div>
        </div>
      </Modal>

      {/* Edit Building Modal */}
      <BuildingFormModal
        isOpen={isBuildingModalOpen}
        onClose={() => setIsBuildingModalOpen(false)}
        onSave={handleSaveBuilding}
        building={building}
      />
    </AdminLayout>
  );
}

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div className="bg-white p-4 lg:p-6 rounded-xl border border-gray-200">
      <p className="text-2xl lg:text-3xl font-bold text-gray-900 mb-0.5 lg:mb-1">{value}</p>
      <p className="text-xs lg:text-sm font-medium text-gray-500 uppercase tracking-wide truncate">
        {label}
      </p>
    </div>
  );
}

// Form types
interface UnitFormData {
  unit_type_id: string;
  template_id: string | null;
  unit_number: string;
  floor: number;
  price: number;
  square_feet: number | null;
  availability_date: string;
}

interface UnitFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UnitFormData) => void;
  unit: Unit | null;
  unitTypes: UnitType[];
  templates: UnitTemplate[];
  slug: string;
}

function UnitFormModal({ isOpen, onClose, onSave, unit, unitTypes, templates, slug }: UnitFormModalProps) {
  const [formData, setFormData] = useState<UnitFormData>({
    unit_type_id: unit?.unit_type_id || '',
    template_id: unit?.template_id || null,
    unit_number: unit?.unit_number || '',
    floor: unit?.floor || 1,
    price: unit?.price || 0,
    square_feet: unit?.square_feet || null,
    availability_date: unit?.availability_date || '',
  });
  const [availableImmediately, setAvailableImmediately] = useState(false);

  // Reset form when unit changes
  useState(() => {
    if (isOpen) {
      setFormData({
        unit_type_id: unit?.unit_type_id || '',
        template_id: unit?.template_id || null,
        unit_number: unit?.unit_number || '',
        floor: unit?.floor || 1,
        price: unit?.price || 0,
        square_feet: unit?.square_feet || null,
        availability_date: unit?.availability_date || '',
      });
      setAvailableImmediately(false);
    }
  });

  // Handle template selection - auto-fills unit type and sq ft
  const handleTemplateChange = (templateId: string) => {
    if (!templateId) {
      setFormData((prev) => ({ ...prev, template_id: null }));
      return;
    }
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      setFormData((prev) => ({
        ...prev,
        template_id: templateId,
        unit_type_id: template.unit_type_id,
        square_feet: template.default_square_feet || prev.square_feet,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleImmediateChange = (checked: boolean) => {
    setAvailableImmediately(checked);
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        availability_date: new Date().toISOString().split('T')[0],
      }));
    }
  };

  const unitTypeOptions = unitTypes.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  const templateOptions = templates.map((t) => ({
    value: t.id,
    label: `${t.name} (${t.unit_type?.name || 'Unknown type'})`,
  }));

  // Get selected template to show its images
  const selectedTemplate = formData.template_id
    ? templates.find((t) => t.id === formData.template_id)
    : null;

  const floorOptions = Array.from({ length: 20 }, (_, i) => ({
    value: String(i + 1),
    label: `Floor ${i + 1}`,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={unit ? 'Edit Unit' : 'Add New Unit'}
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="w-full sm:w-auto">
            {unit ? 'Save Changes' : 'Save Unit'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Template selector - optional, auto-fills type and sq ft */}
        {templates.length > 0 && (
          <Select
            label="Template (optional)"
            options={templateOptions}
            placeholder="No template - manual entry"
            value={formData.template_id || ''}
            onChange={(e) => handleTemplateChange(e.target.value)}
          />
        )}

        {/* Template Images Preview */}
        {selectedTemplate && selectedTemplate.images && selectedTemplate.images.length > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs font-medium text-blue-700 mb-2">
              Template Photos ({selectedTemplate.images.length})
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {selectedTemplate.images.slice(0, 6).map((img, idx) => (
                <div key={idx} className="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-gray-100">
                  <img
                    src={img.image_url}
                    alt={img.image_type}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {selectedTemplate.images.length > 6 && (
                <div className="flex-shrink-0 w-16 h-16 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                  +{selectedTemplate.images.length - 6}
                </div>
              )}
            </div>
            <p className="text-xs text-blue-600 mt-2">
              These photos will be used for this unit
            </p>
          </div>
        )}

        {/* No template images notice */}
        {selectedTemplate && (!selectedTemplate.images || selectedTemplate.images.length === 0) && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <ImageIcon size={16} className="text-yellow-600 flex-shrink-0" />
            <p className="text-xs text-yellow-700">
              This template has no photos yet. <Link to={`/admin/buildings/${slug}/templates/${selectedTemplate.id}`} className="underline">Add photos</Link>
            </p>
          </div>
        )}

        {/* Floor first - drives unit number convention */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Floor"
            options={floorOptions}
            value={String(formData.floor)}
            onChange={(e) => {
              const newFloor = parseInt(e.target.value);
              setFormData((prev) => {
                // If unit number is empty or just a floor prefix, update it
                const unitNum = prev.unit_number;
                const shouldUpdateUnit = !unitNum || /^\d{1,2}$/.test(unitNum);
                return {
                  ...prev,
                  floor: newFloor,
                  unit_number: shouldUpdateUnit ? String(newFloor) : unitNum,
                };
              });
            }}
            required
          />

          <Select
            label="Unit Type"
            options={unitTypeOptions}
            placeholder="Select type"
            value={formData.unit_type_id}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, unit_type_id: e.target.value }))
            }
            disabled={!!formData.template_id}
            required
          />
        </div>

        <Input
          label="Unit Number"
          placeholder={`e.g. ${formData.floor}01, ${formData.floor}02`}
          value={formData.unit_number}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, unit_number: e.target.value }))
          }
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Monthly Rent"
            type="number"
            placeholder="2150"
            value={formData.price || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                price: parseFloat(e.target.value) || 0,
              }))
            }
            required
          />

          <Input
            label="Sq Ft (optional)"
            type="number"
            placeholder="650"
            value={formData.square_feet || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                square_feet: e.target.value ? parseInt(e.target.value) : null,
              }))
            }
          />
        </div>

        <div className="space-y-2">
          <Input
            label="Available From"
            type="date"
            value={formData.availability_date}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                availability_date: e.target.value,
              }))
            }
            disabled={availableImmediately}
            required
          />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={availableImmediately}
              onChange={(e) => handleImmediateChange(e.target.checked)}
              className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            Available immediately
          </label>
        </div>
      </form>
    </Modal>
  );
}

// Agent form types
interface AgentFormData {
  name: string;
  email: string;
  phone: string | null;
  title: string | null;
  is_primary: boolean;
}

interface AgentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: AgentFormData) => void;
  agent: LeasingAgent | null;
}

function AgentFormModal({ isOpen, onClose, onSave, agent }: AgentFormModalProps) {
  const [formData, setFormData] = useState<AgentFormData>({
    name: agent?.name || '',
    email: agent?.email || '',
    phone: agent?.phone || null,
    title: agent?.title || null,
    is_primary: agent?.is_primary || false,
  });

  // Reset form when agent changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: agent?.name || '',
        email: agent?.email || '',
        phone: agent?.phone || null,
        title: agent?.title || null,
        is_primary: agent?.is_primary || false,
      });
    }
  }, [isOpen, agent]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={agent ? 'Edit Agent' : 'Add Leasing Agent'}
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="w-full sm:w-auto">
            {agent ? 'Save Changes' : 'Add Agent'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Full Name"
          placeholder="John Smith"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          required
        />

        <Input
          label="Email"
          type="email"
          placeholder="john@beauxproperties.ca"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Phone (optional)"
            type="tel"
            placeholder="416-555-1234"
            value={formData.phone || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value || null }))}
          />

          <Input
            label="Title (optional)"
            placeholder="Leasing Manager"
            value={formData.title || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value || null }))}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={formData.is_primary}
            onChange={(e) => setFormData((prev) => ({ ...prev, is_primary: e.target.checked }))}
            className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
          />
          Primary contact (shown first on public page)
        </label>
      </form>
    </Modal>
  );
}

// Building form types
interface BuildingFormData {
  name: string;
  address: string;
  city: string;
  postal_code: string | null;
  description: string | null;
  leasing_email: string;
  leasing_phone: string | null;
  hero_image_id: string | null;
  amenities: string[];
}

interface BuildingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BuildingFormData) => void;
  building: Building | null;
}

function BuildingFormModal({ isOpen, onClose, onSave, building }: BuildingFormModalProps) {
  const [formData, setFormData] = useState<BuildingFormData>({
    name: building?.name || '',
    address: building?.address || '',
    city: building?.city || 'Toronto',
    postal_code: building?.postal_code || '',
    description: building?.description || '',
    leasing_email: building?.leasing_email || '',
    leasing_phone: building?.leasing_phone || '',
    hero_image_id: (building as any)?.hero_image_id || null,
    amenities: building?.amenities || [],
  });

  // Reset form when building changes
  useEffect(() => {
    if (isOpen && building) {
      setFormData({
        name: building.name || '',
        address: building.address || '',
        city: building.city || 'Toronto',
        postal_code: building.postal_code || '',
        description: building.description || '',
        leasing_email: building.leasing_email || '',
        leasing_phone: building.leasing_phone || '',
        hero_image_id: (building as any)?.hero_image_id || null,
        amenities: building.amenities || [],
      });
    }
  }, [isOpen, building]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Building"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="w-full sm:w-auto">
            Save Changes
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Hero Image Picker */}
        {building && (
          <ImagePicker
            buildingId={building.id}
            selectedImageId={formData.hero_image_id}
            onSelect={(imageId) =>
              setFormData((prev) => ({ ...prev, hero_image_id: imageId }))
            }
            category="exterior"
          />
        )}

        <Input
          label="Building Name"
          placeholder="66 Broadway"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          required
        />

        <Input
          label="Address"
          placeholder="66 Broadway Ave"
          value={formData.address}
          onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
          required
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="City"
            placeholder="Toronto"
            value={formData.city}
            onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
            required
          />

          <Input
            label="Postal Code"
            placeholder="M4P 1T4"
            value={formData.postal_code || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, postal_code: e.target.value || null }))}
          />
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            rows={3}
            placeholder="A modern building with excellent amenities..."
            value={formData.description || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value || null }))}
          />
        </div>

        {/* Amenities Editor */}
        <AmenitiesEditor
          amenities={formData.amenities}
          onChange={(amenities) => setFormData((prev) => ({ ...prev, amenities }))}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Leasing Email"
            type="email"
            placeholder="leasing@beauxproperties.ca"
            value={formData.leasing_email}
            onChange={(e) => setFormData((prev) => ({ ...prev, leasing_email: e.target.value }))}
            required
          />

          <Input
            label="Leasing Phone"
            type="tel"
            placeholder="416-555-1234"
            value={formData.leasing_phone || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, leasing_phone: e.target.value || null }))}
          />
        </div>
      </form>
    </Modal>
  );
}
